// 5-card KPI strip for the BMD report dashboard: pastel backgrounds, big
// number, descriptive subtitle. "Questions" is split into "Attempted" and
// "Not Attempted" so the tutor can read engagement separately from test size.
//
// SCOPE SELECTOR. Every number here counted the base test only, so once a
// prerequisite test ran the student could answer another 150 questions without
// a single figure moving. The dropdown swaps the WHOLE SET between the base
// test and the base test plus its prerequisite test - the same two options,
// worded the same way, as the radar below it.
//
// It swaps whole objects rather than individual fields on purpose: mixing a
// base-scoped count with an overall-scoped one in the same strip would be
// invisible and wrong. Which is why the English-only counts - `answers` and
// `awaiting_marking` - are read off the scoped object too, rather than passed
// in beside it: as a prop, "awaiting marking" kept showing the overall figure
// while the four cards next to it had switched to the base test.

import React from "react"
import type { BmdReportKpi } from "../types/bmd-report.types"
import { useReportCopy } from "../services/report-copy"

// `kpi.topics` counts session topic rows - the units a tutor picks when building
// a test. What those ARE differs by subject: a maths block is the unit the
// business calls a cluster, an English block is one topic and several sit inside
// one cluster. The word comes from the copy context; the field keeps the name
// the API gives it.

type CardStyle = {
  label: string
  bgColor: string
  valueColor: string
}

// Bootstrap doesn't ship purple as a theme color in this build, so the SBs
// card uses inline pastel values to stay consistent with the others.
const CARD_STYLES: Record<string, CardStyle> = {
  attempted:    { label: "Attempted",     bgColor: "#fde2e1", valueColor: "#b42318" },
  notAttempted: { label: "Not Attempted", bgColor: "#fff4d6", valueColor: "#92400e" },
  correct:      { label: "Correct",       bgColor: "#d1fae5", valueColor: "#047857" },
  incorrect:    { label: "Incorrect",     bgColor: "#dbeafe", valueColor: "#1d4ed8" },
  sbs:          { label: "Skill Builders Queued", bgColor: "#ede9fe", valueColor: "#6d28d9" },
  // English only: answers on manually-marked questions still waiting for a tutor. They are
  // excluded from accuracy rather than counted wrong, so without this card the tutor would see
  // a test that does not add up and no reason why.
  awaiting:     { label: "Awaiting Marking", bgColor: "#e0f2fe", valueColor: "#0369a1" },
}

type CardProps = {
  styleKey: keyof typeof CARD_STYLES
  value: number | string
  subtitle: React.ReactNode
}

const KpiCard: React.FC<CardProps> = ({ styleKey, value, subtitle }) => {
  const s = CARD_STYLES[styleKey]
  return (
    <div
      className="rounded p-3 flex-fill"
      style={{ minWidth: "180px", backgroundColor: s.bgColor }}
    >
      <div className="text-uppercase fw-bold font-size-12 text-muted">
        {s.label}
      </div>
      <div
        className="fw-bold"
        style={{ fontSize: "32px", color: s.valueColor }}
      >
        {value}
      </div>
      <div className="font-size-13 text-muted">{subtitle}</div>
    </div>
  )
}

export type BmdReportKpiCardsProps = {
  /** Base test only. */
  kpi: BmdReportKpi | null
  /**
   * Base test plus the prerequisite test. Optional so an older response, or a
   * caller that has not been updated, simply loses the selector rather than
   * rendering an empty strip.
   */
  kpiOverall?: BmdReportKpi | null
  /**
   * Drops the Skill Builders card. English has no Skill Builders yet, and the card would read
   * "0 queued, 0 completed" - which claims nothing is owed rather than that nothing can be
   * offered. Delete the prop at the call site when English skill builders land.
   */
  hideSkillBuilders?: boolean
}

type KpiScope = "base" | "overall"

/**
 * Which scope the strip opens on.
 *
 * Overall, on the PM's instruction: once a prerequisite test has been sat, the
 * base-test numbers are no longer the whole story, and a tutor who never opens
 * the dropdown was reading a partial picture as though it were the total. The
 * base test is still one click away.
 *
 * `kpi_overall` can be absent - an older backend omits it - in which case the
 * selector is not drawn and this falls back to the base numbers.
 */
const DEFAULT_KPI_SCOPE: KpiScope = "overall"

export const BmdReportKpiCards: React.FC<BmdReportKpiCardsProps> = ({
  kpi,
  kpiOverall,
  hideSkillBuilders = false,
}) => {
  const { kpiGroupNoun } = useReportCopy()
  const [scope, setScope] = React.useState<KpiScope>(DEFAULT_KPI_SCOPE)

  // Hooks run before the early return, so the selector cannot change the hook
  // order between renders.
  const canSwitch = !!kpiOverall
  const active = scope === "overall" && kpiOverall ? kpiOverall : kpi

  if (!active) {
    return (
      <div className="text-muted font-size-13">No diagnostic data yet.</div>
    )
  }

  // Mirrors the Correct card. accuracy_pct is correct over answered, so the
  // complement is the incorrect share of the same base. Taken as 100 minus
  // accuracy rather than rounded separately, so the two cards always add up to
  // 100 instead of landing on 34 and 67.
  //
  // The chain and FM counts are still on the response and unused here.
  const answered = active.correct + active.incorrect_total

  // Both cards below count ANSWERS while the two above count QUESTIONS, and for a subject that
  // marks a question in several parts those are different numbers: 6 questions can yield 14
  // answers, so "Correct 12 · 85% accuracy" read as 12 out of 6 with nothing on screen to
  // explain it. Naming the denominator is all it takes. `answers` arrives only from a subject
  // where the two grains differ, so the maths wording is untouched.
  const perPart = active.answers != null && active.answers !== active.questions
  const answersLabel = `${answered} answer${answered === 1 ? "" : "s"}`
  // accuracy_pct arrives rounded to one decimal, so the complement has to be rounded back to
  // one decimal too - 100 - 83.3 is 16.700000000000003 in binary floating point, and that is
  // exactly what the card displayed.
  const incorrectPct = Math.round((100 - active.accuracy_pct) * 10) / 10
  const correctSubtitle = perPart
    ? `${active.accuracy_pct}% of ${answersLabel}`
    : `${active.accuracy_pct}% accuracy`
  const incorrectSubtitle =
    answered === 0
      ? "no answers yet"
      : perPart
        ? `${incorrectPct}% of ${answersLabel}`
        : `${incorrectPct}% of answers`

  const totalLabel = `of ${active.questions} total`

  return (
    <div className="mb-4">
      {canSwitch ? (
        <div className="d-flex align-items-center gap-2 mb-2">
          <label
            htmlFor="bmd-kpi-scope"
            className="text-muted font-size-12 mb-0"
          >
            Showing:
          </label>
          <select
            id="bmd-kpi-scope"
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={scope}
            onChange={(e) => setScope(e.target.value as KpiScope)}
          >
            <option value="base">Base test</option>
            <option value="overall">Overall (with prerequisite test)</option>
          </select>
        </div>
      ) : null}

      <div className="d-flex flex-wrap gap-3">
      <KpiCard
        styleKey="attempted"
        value={active.questions_attempted}
        subtitle={`${totalLabel} · across ${active.topics} ${
          active.topics === 1 ? kpiGroupNoun.one : kpiGroupNoun.many
        }`}
      />
      <KpiCard
        styleKey="notAttempted"
        value={active.questions_not_attempted}
        subtitle={
          active.questions_not_attempted === 0
            ? "all questions answered"
            : `unanswered or skipped`
        }
      />
      <KpiCard
        styleKey="correct"
        value={active.correct}
        subtitle={correctSubtitle}
      />
      <KpiCard
        styleKey="incorrect"
        value={active.incorrect_total}
        subtitle={incorrectSubtitle}
      />
      {hideSkillBuilders ? null : (
        <KpiCard
          styleKey="sbs"
          value={active.sb_queued}
          subtitle={`${active.sb_completed} completed`}
        />
      )}
      {active.awaiting_marking ? (
        <KpiCard
          styleKey="awaiting"
          value={active.awaiting_marking}
          subtitle="not counted in accuracy yet"
        />
      ) : null}
      </div>
    </div>
  )
}

export default BmdReportKpiCards
